import Card from "components/card";
import React, { useEffect, useState } from "react";
import { IoPencil, IoCheckmark, IoClose } from "react-icons/io5";
import { MdBadge } from "react-icons/md";
import avatar from "assets/img/avatars/avatar.webp";

const ClientHeader = ({ client, saveClientSection }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState({
    name: "",
    profileImage: null,
    profileImagePreview: "",
  });

  useEffect(() => {
    if (!isEditing) {
      setLocalData({
        name: client?.name || "",
        profileImage: null,
        profileImagePreview: client?.profileImage || "",
      });
    }
  }, [client, isEditing]);

  const profileImage =
    localData.profileImagePreview || client?.profileImage || avatar;

  const handleFieldChange = (field, value) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setLocalData((prev) => ({
      ...prev,
      profileImage: file,
      profileImagePreview: preview,
    }));
  };

  const handleCancel = () => {
    if (localData.profileImage && localData.profileImagePreview) {
      URL.revokeObjectURL(localData.profileImagePreview);
    }
    setLocalData({
      name: client?.name || "",
      profileImage: null,
      profileImagePreview: client?.profileImage || "",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveClientSection(
        {
          name: localData.name,
          profileImage: localData.profileImage,
        },
        "Profile Header"
      );
      setIsEditing(false);
    } catch (error) {
      // Error is handled by saveClientSection
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card extra="w-full rounded-[24px] border border-gray-200 bg-slate-50 p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy-900 dark:text-white">Profile Header</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Basic client identity and avatar.</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-600 disabled:opacity-60"
              >
                <IoClose className="text-base" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-60"
              >
                <IoCheckmark className="text-base" />
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              <IoPencil className="text-base" />
              Edit
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="h-28 w-28 overflow-hidden rounded-full border border-white bg-white shadow-sm">
              <img src={profileImage} alt="Client avatar" className="h-full w-full object-cover" />
            </div>
            {isEditing && (
              <label className="absolute -bottom-2 right-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-brand-500 text-white shadow-lg transition hover:bg-brand-600">
                <IoPencil className="text-lg" />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            )}
          </div>

          <div className="min-w-0">
            {isEditing ? (
              <input
                type="text"
                value={localData.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                placeholder="Client name"
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-2xl font-semibold text-navy-700 outline-none transition hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              />
            ) : (
              <h1 className="truncate text-3xl font-bold text-navy-900 dark:text-white">
                {client?.name || "Client Name"}
              </h1>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-sm font-semibold text-slate-700 shadow-sm dark:bg-navy-900 dark:text-slate-200">
                <MdBadge className="h-4 w-4" />
                Client
              </span>
            </div>
          </div>
        </div>

        <div className="grid w-full gap-4 sm:grid-cols-3 xl:w-auto">
          <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-700">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Source</p>
            <p className="mt-2 text-lg font-semibold text-navy-900 dark:text-white">{client?.source || "—"}</p>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-700">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Status</p>
            <p className="mt-2 text-lg font-semibold text-navy-900 dark:text-white">{client?.status || "—"}</p>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-700">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Priority</p>
            <p className="mt-2 text-lg font-semibold text-navy-900 dark:text-white">{client?.priority || "—"}</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ClientHeader;
